import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as query from '../query'

import { Commented } from './commented'
import * as fn from './fn'
import * as instance from './instance'
import * as primitive from './primitive'

/**
 * A special data structure used in various parts of the DMS (especially
 * {@link query.Query}) to represent a value
 *
 * This can either be a straightforward {@link primitive.Primitive} value or a
 * computation represented by a {@link fn.Fn} or an {@link instance.Instance}
 */
export type Expression<T = any> =
  | primitive.Primitive<T>
  | fn.Fn<T>
  | instance.Instance<T>
  | Commented<T>
export namespace Expression {
  export const isExpression: TypeChecker<Expression> = TypeChecker.Factory.lazy(
    () =>
      // NOTE: ordered by balance of speed and likelihood to achieve optimal
      // overall speed
      TypeChecker.Factory.union(
        primitive.Primitive.isPrimitive,
        Commented.isCommented,
        fn.Fn.isFn,
        instance.Instance.isInstance,
      ),
  ).usingAlias('dms.Expression')
  export const assertIsExpression: TypeAsserter<Expression> =
    TypeAsserter.Factory.from(isExpression)
  export function getTypeChecker<T extends primitive.Primitive>(
    isPrimitive: TypeChecker<T>,
  ): TypeChecker<Expression<T>> {
    // NOTE: we don't restrict the Fn.isFn and Instance.isInstance check because
    // that would be too complicated, so it is just not worth it
    return TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(
        isPrimitive,
        fn.Fn.isFn as TypeChecker<fn.Fn<T>>,
        instance.Instance.isInstance as TypeChecker<instance.Instance<T>>,
      ),
    )
  }
  /**
   * Type of the result of the given {@link Expression}
   *
   * @example
   * type ExpressionResult = Expression.Result<Expression<string | number>>
   * // ===
   * type ExpressionResult = string | number
   */
  export type Result<TExpression extends Expression> =
    TExpression extends Expression<infer T> ? T : unknown

  /**
   * Converts a union of {@link Expression} types into a single
   * {@link Expression} type that can produce the union of results from the
   * given {@link Expression} types
   *
   * @example
   * type FlatExpression = Expression.Flatten<Expression<string> | Expression<number>>
   * // ===
   * type FlatExpression = Expression<string | number>
   */
  export type Flatten<TExpression extends Expression> = Expression<
    Result<TExpression>
  >

  /**
   * Returns all expressions from the given {@link query.Query}
   */
  export function* allFrom(_query: query.Query): Generator<GeneratorItem> {
    yield* allWithin(...query.Query.getTopLevelExpressions(_query))
  }

  /**
   * Returns all expressions within the given expressions. This includes the
   * given expressions and any expressions nested in those expressions
   */
  export function* allWithin(
    ...expressions: Expression[]
  ): Generator<GeneratorItem> {
    let expression: Expression | undefined
    while ((expression = expressions.pop()) !== undefined) {
      let _pruned = false
      yield { expression, prune: () => (_pruned = true) }
      if (_pruned || primitive.Primitive.isPrimitive(expression)) {
        continue
      }
      if (Commented.isCommented(expression)) {
        expressions.push(Commented.getExpressionWithoutComment(expression))
      } else if (instance.Instance.isInstance(expression)) {
        // TODO: get expressions in instance
      } else {
        // Fn.isFn is the slowest type checker, so we make it the else condition
        expressions.push(...fn.Fn.expressionsInArgs(expression))
      }
    }
  }

  /**
   * @see {@link allFrom}, {@link allWithin}
   */
  export interface GeneratorItem {
    expression: Expression
    /**
     * Skips any nested expressions in this expression
     */
    prune: () => void
  }
}
